import { levelName } from "building-generation/elements/level/levelTools";
import { getWallPath } from "building-generation/elements/walls/wall";
import { solidWallSubstrateLayer } from "building-generation/elements/walls/wallLayers";
import { materialKeyMeasureName } from "building-generation/utils/materialKeyMeasureName";
import { traverseWallAreas } from "building-geometry/metrics/walls/traverseWallAreas";
import type { Building } from "building-types/building/buildingTypes";
import { WallAreaType } from "building-types/dataSequences/builtin/wallAreaTraversalTypes";
import type {
  WallAreaKAM,
  WallAreaKAMOptions,
  WallAreaKeyAttributes,
  WallAreaKeyMeasures,
  WallKAM,
  WallKAMOptions,
  WallKeyAttributes,
  WallKeyMeasures,
} from "building-types/dataSequences/builtin/WallKAM";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { dataSequenceUnitConversionFunctions } from "building-types/dataSequences/scheduleSettingsTable";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import { WallType } from "building-types/walls/wallEnums";
import type { SolidWall, Wall } from "building-types/walls/wallTypes";
import { SolidWallKind } from "building-types/walls/wallTypes";
import type { InchesSquared } from "common/types/nominalNumbersTypes";
import { ensureDefined } from "common/utils/assertOfType";
import { inchesSquaredNew, numAdd } from "common/utils/nominalNumbers";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import {
  keyAttributeNew,
  keyMeasureNew,
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import { ascendingStringSortCompare } from "geom/utils";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  SortBy,
  aggregatorColumnNew,
  categoryColumnNew,
  configurableTableNew,
  repeaterColumnNew,
  stringColumnNew,
  stringConverterColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { dimension1fractionFunc } from "lib/house/dataSequences/dataSequenceFormatting";
import type { Access } from "./doors";

interface ProcureWall {
  type: string;
  levelId: string;
  levelName: string;
  floorIndex: number;
  enclosure: Access;
  width: number;
  walls: SolidWall[];
}

export function makeWallsTables(
  wallSystems: Building["walls"],
  levels: Building["levels"],
): { [key: string]: ConfigurableTable } {
  const newTables: { [key: string]: ConfigurableTable } = {};

  for (const levelId in wallSystems) {
    const wallSystem = wallSystems[levelId];
    const walls = wallSystem.walls;

    newTables[`Wall level ${levels[levelId].floorIndex + 1}`] = configurableTableNew<Wall, SolidWall>({
      iterables: walls,
      requirement: isNotVoidOrNonEnclosure,
      columns: [
        categoryColumnNew("Wall"),
        repeaterColumnNew({
          name: "Level",
          value: `${levels[levelId].floorIndex + 1}`,
        }),
        stringColumnNew({
          name: "Enclosure",
          fieldFunc: (wall) => wallAccess(wall),
          sortBy: SortBy.Descending,
        }),
        stringConverterColumnNew({
          name: "Width",
          fieldFunc: (wall) => solidWallSubstrateLayer(wall).thickness,
          stringFormatter: dimension1fractionFunc(2),
        }),
      ],
      aggregator: aggregatorColumnNew({
        name: "Length",
        aggregatorFunc: (wall) => getWallPath(wall).length,
        stringFormatter: dimension1fractionFunc(1),
        sortBy: SortBy.Ascending,
      }),
    });
  }

  return newTables;
}

function isNotVoidOrNonEnclosure(wall: Wall): SolidWall | null {
  const { wallType } = wall;

  if (wallType === WallType.VOID) {
    return null;
  }

  const solidWall = wall as SolidWall;
  const { wallKind } = solidWall;

  if (wallKind === SolidWallKind.NONENCLOSURE) {
    return null;
  }

  return wall as SolidWall;
}

function wallAccess(wall: Wall): Access {
  const { rightRoom, leftRoom } = getWallPath(wall);

  if (rightRoom && !isWalledRoomEnclosure[rightRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  if (!rightRoom) {
    return "exterior";
  }
  if (leftRoom && !isWalledRoomEnclosure[leftRoom.attributes.roomEnclosure]) {
    return "exterior";
  }
  if (!leftRoom) {
    return "exterior";
  }
  return "interior";
}

interface WallArea {
  type: WallAreaType;
  areaName: string;
  detail: string;
  area: InchesSquared;
}

const converSqInToSqFt = ensureDefined(dataSequenceUnitConversionFunctions["SQ IN"]["SQ FT"]);

export function makeWallAreaTables(building: Building): ConfigurableTable {
  const areas = computeWallAreas(building);

  return configurableTableNew<WallArea, WallArea>({
    iterables: areas,
    requirement: (wa: WallArea) => (wa.area > 0 ? wa : null),
    columns: [
      categoryColumnNew("Wall Area"),
      stringColumnNew({
        name: "Material Name",
        fieldFunc: (counter) => counter.areaName,
      }),
    ],
    aggregator: aggregatorColumnNew({
      name: "Area (SF)",
      aggregatorFunc: (counter) => (counter.area ? Math.ceil(converSqInToSqFt(counter.area ?? 0)) : 0),
    }),
  });
}

type WallAreaDetail = { area: InchesSquared; material: string; detail: string };

/** @deprecated - use the Building Query API instead */
function computeWallAreas(building: Building, breakoutDetails: boolean = false): WallArea[] {
  const { exteriorPalette } = building;
  let garageWallArea = inchesSquaredNew(0);
  const interiorWallAreas: { [key: string]: WallAreaDetail } = {};
  const exteriorWallAreas: { [key: string]: WallAreaDetail } = {};
  const wallAreaIterator = traverseWallAreas(building);
  let wallAreaResult = wallAreaIterator.next();
  while (!wallAreaResult.done) {
    const wallArea = wallAreaResult.value;
    const detail = breakoutDetails ? wallArea.detail : "ALL";
    if (wallArea.wallType === WallAreaType.EXTERIOR_WALL) {
      addMaterialArea(
        exteriorWallAreas,
        materialKeyMeasureName(exteriorPalette, wallArea.exteriorMaterial),
        wallArea.area,
        detail,
      );
    } else if (wallArea.wallType === WallAreaType.INTERIOR_WALL) {
      addMaterialArea(interiorWallAreas, "Interior wall area", wallArea.area, detail);
    } else if (wallArea.wallType === WallAreaType.GARAGE_WALL) {
      garageWallArea = numAdd(garageWallArea, wallArea.area);
    }
    wallAreaResult = wallAreaIterator.next();
  }

  // TODO: add windowInfills areas

  const areas: WallArea[] = [
    { areaName: "Garage wall area", area: garageWallArea, type: WallAreaType.GARAGE_WALL, detail: "ALL" },
  ];

  for (const name in exteriorWallAreas) {
    areas.push({
      areaName: exteriorWallAreas[name].material,
      area: exteriorWallAreas[name].area,
      type: WallAreaType.EXTERIOR_WALL,
      detail: exteriorWallAreas[name].detail,
    });
  }
  for (const name in interiorWallAreas) {
    areas.push({
      areaName: interiorWallAreas[name].material,
      area: interiorWallAreas[name].area,
      type: WallAreaType.INTERIOR_WALL,
      detail: interiorWallAreas[name].detail,
    });
  }

  areas.sort((a, b) => ascendingStringSortCompare(a.areaName, b.areaName));
  return areas.filter((wa) => wa.area > 0);
}

function addMaterialArea(
  wallAreas: { [key: string]: WallAreaDetail },
  material: string,
  area: InchesSquared,
  detail: string | undefined,
) {
  const key = material + (detail || "");
  if (detail !== "ALL") {
    // Broken-out details must not also emit a material-level "ALL" rollup:
    // aggregating rows (e.g. a schedule with the detail column hidden) would
    // sum rollup + details and double-count the area.
    if (wallAreas[key]?.area) {
      const totalArea = (wallAreas[key].area + area) as InchesSquared;
      wallAreas[key].area = totalArea;
    } else {
      wallAreas[key] = { area, material, detail: detail || "" };
    }
  } else if (wallAreas[material]?.area) {
    const totalArea = (wallAreas[material].area + area) as InchesSquared;
    wallAreas[material].area = totalArea;
  } else {
    wallAreas[material] = { area, material, detail: "ALL" };
  }
}

export function makeWallKAM({ type, levelName, enclosure, width, walls = [] }: ProcureWall): WallKAM {
  return {
    type: "WALL",
    attributes: {
      type: keyAttributeNew(type),
      baseConstraint: keyAttributeNew(levelName),
      level: keyAttributeNew(levelName),
      enclosure: keyAttributeNew(enclosure),
      width: keyAttributeNew(width.toString(), "INCHES"),
      entityName: keyAttributeNew(`${width}-${enclosure}`),
    },
    measures: {
      length: keyMeasureNew(
        walls.reduce((acc, wall) => acc + getWallPath(wall).length, 0),
        "FEET",
        "INCHES",
      ),
      width: keyMeasureNew(width, "INCHES"),
    },
    handles: walls.map(buildingEntityPickHandle),
  };
}
export const EMPTY_WALL_KAM: WallKAM = {
  type: "WALL",
  attributes: {
    type: keyAttributeNew(""),
    baseConstraint: keyAttributeNew(""),
    level: keyAttributeNew(""),
    enclosure: keyAttributeNew(""),
    width: keyAttributeNew("", "INCHES"),
    entityName: keyAttributeNew(""),
  },
  measures: {
    length: keyMeasureNew(0, "FEET"),
    width: keyMeasureNew(0, "INCHES"),
  },
  handles: [],
};
export function makeDefaultWallKAMOptions(): WallKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      length: keyMeasureOptionsNew({ unit: "FEET" }),
      width: keyMeasureOptionsNew({ unit: "INCHES" }),
    },
  };
}
export const defaultWallKAMSort = (a: WallKAM, b: WallKAM) => {
  return (
    b.attributes.enclosure.value.localeCompare(a.attributes.enclosure.value) ||
    a.measures.length.value - b.measures.length.value
  );
};

const WALL_KIND_MAPPING = {
  [SolidWallKind.NONENCLOSURE]: "",
  [SolidWallKind.INTERIOR]: "INTERIOR",
  [SolidWallKind.EXTERIOR]: "EXTERIOR",
};

/** @deprecated - use the Building Query API instead */
export function buildingWallKAMs(building: Building, filter?: BuildingEntityFilter): WallKAM[] {
  const { walls, levels } = building;
  const wallsByLevel: { [key: string]: ProcureWall } = {};

  for (const levelId in walls) {
    const wallSystem = walls[levelId];
    const wallTable = wallSystem.walls;
    const levelKey = ensureDefined(levelName(levels[levelId]));

    for (const wallId in wallTable) {
      const wall = isNotVoidOrNonEnclosure(wallTable[wallId]);
      if (wall) {
        if (filter && !filter(wall)) {
          continue;
        }
        const width = solidWallSubstrateLayer(wall)?.thickness;
        const enclosure = wallAccess(wall);
        const type = WALL_KIND_MAPPING[wall.wallKind];
        const id = `${levelId}-${enclosure}-${width}`;
        if (!wallsByLevel[id]) {
          wallsByLevel[id] = {
            type,
            enclosure,
            levelId,
            levelName: levelKey,
            floorIndex: levels[levelId].floorIndex,
            width,
            walls: [wall] as SolidWall[],
          };
        } else {
          wallsByLevel[id].walls.push(wall);
        }
      }
    }
  }

  return Object.values(wallsByLevel).map(makeWallKAM);
}

/** @deprecated - use the Building Query API instead */
export function buildingWallAreaKAMs(building: Building): WallAreaKAM[] {
  return computeWallAreas(building, true).map((wa) => makeWallAreaKAM(wa));
}

export function makeWallAreaKAM(wall: WallArea): WallAreaKAM {
  return {
    type: "WALL_AREA",
    attributes: {
      type: keyAttributeNew(WallAreaType[wall.type]),
      name: keyAttributeNew(wall.areaName),
      entityName: keyAttributeNew(wall.areaName),
      detail: keyAttributeNew((wall.detail ?? "").toUpperCase()),
    },
    measures: {
      area: keyMeasureNew(wall.area, "SQ FT", "SQ IN"),
    },
    handles: [],
  };
}

export const EMPTY_WALL_AREA_KAM: WallAreaKAM = {
  type: "WALL_AREA",
  attributes: {
    type: keyAttributeNew(""),
    name: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
    detail: keyAttributeNew(""),
  },
  measures: {
    area: keyMeasureNew(0, "SQ FT"),
  },
  handles: [],
};
export function makeDefaultWallAreaKAMOptions(): WallAreaKAMOptions {
  return {
    attributes: {
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      area: keyMeasureOptionsNew({ unit: "SQ FT" }),
    },
  };
}
export const defaultWallAreaKAMSort = (a: WallAreaKAM, b: WallAreaKAM) => {
  return (
    b.attributes.type.value.localeCompare(a.attributes.type.value) ||
    b.attributes.name.value.localeCompare(a.attributes.name.value) ||
    a.attributes.detail.value.localeCompare(b.attributes.detail.value) ||
    a.measures.area.value - b.measures.area.value
  );
};

export const WALL_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<WallKeyAttributes, WallKeyMeasures> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    type: {
      hidden: true,
    },
    baseConstraint: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_WALL: (keyof WallKeyAttributes)[] = ["width", "enclosure"];

export const WALL_AREA_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<
  WallAreaKeyAttributes,
  WallAreaKeyMeasures
> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    type: {
      hidden: true,
    },
  },
};

// Attributes used in procure entityHash
export const DEFINES_WALL_AREA: (keyof WallAreaKeyAttributes)[] = ["name", "detail"];

export type WallSubEntity = "WALL_AREA";
